import { useEffect, useId, useRef, useState } from "react";
import type { ChangeEvent, FormEvent, KeyboardEvent } from "react";
import Button from "../../../../design-system/primitives/components/Button/Button";
import Textarea from "../../../../design-system/primitives/components/Textarea/Textarea";
import { icons } from "../../../../design-system/primitives/icons";
import styles from "./chatParts.css";

const ArrowUpIcon = icons["arrow-up"];

type PromptInputProps = {
  onSubmit: (text: string) => void;
  busy: boolean;
  maxLength: number;
  autoFocus?: boolean;
  variant?: "stacked" | "inline";
  placeholder?: string;
};

const LABEL = "Deine Frage an den KI-Assistenten";

export const PromptInput = ({
  onSubmit,
  busy,
  maxLength,
  autoFocus,
  variant = "stacked",
  placeholder = "Frag etwas zu diesem Spark …",
}: PromptInputProps) => {
  const [value, setValue] = useState("");
  const fieldName = `spark-chat-input${useId()}`;

  useEffect(() => {
    if (autoFocus) document.getElementById(fieldName)?.focus();
  }, [autoFocus, fieldName]);

  const refocus = useRef(false);
  useEffect(() => {
    if (busy || !refocus.current) return;
    refocus.current = false;
    document.getElementById(fieldName)?.focus();
  }, [busy, fieldName]);

  const canSend = !busy && value.trim().length > 0;

  const submit = () => {
    if (!canSend) return;
    refocus.current =
      variant === "inline" && document.activeElement?.id === fieldName;
    onSubmit(value.trim());
    setValue("");
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (!(event.target instanceof HTMLTextAreaElement)) return;
    if (event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  const onChange = (event: ChangeEvent<HTMLTextAreaElement>) =>
    setValue(event.target.value);

  if (variant === "inline") {
    return (
      <form
        className={styles.inlinePromptForm}
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.promptField}>
          <Textarea
            name={fieldName}
            aria-label={LABEL}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            maxLength={maxLength}
            disabled={busy}
            variant="bare"
            rows={1}
            autoGrow
          />
        </div>
        <button
          type="submit"
          className={styles.sendButton}
          aria-label="Senden"
          disabled={!canSend}
        >
          <ArrowUpIcon size={24} aria-hidden />
        </button>
      </form>
    );
  }

  return (
    <form
      className={styles.promptForm}
      onSubmit={handleSubmit}
      onKeyDown={handleKeyDown}
    >
      <div className={styles.promptField}>
        <Textarea
          name={fieldName}
          label={LABEL}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          maxLength={maxLength}
          rows={2}
        />
      </div>
      <Button
        type="submit"
        title="Senden"
        size="small"
        iconRight="arrow-up"
        disabled={!canSend}
      />
    </form>
  );
};
